import { useQuery } from '@tanstack/react-query';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ALERT_LEVELS, QUOTAS } from '@os/shared';
import { useApi } from '../../app/providers';
import type { Org } from '../../lib/api';
import { Button } from '../../ui';
import { cn } from '../../ui/cn';
import { PageSkeleton } from '../../ui/states';
import { Section } from '../dashboard/cards';
import { formatBytes } from './format';

/**
 * One quota as a gauge: its name, a bar that never leaves its range (the words say how far past it is), a tick at
 * each alert level, and the value, limit and percentage written out (color is never the only sign).
 */
function Gauge({ label, value, limit, ratio, text, children }: { label: string; value: number; limit: number; ratio: number; text: string; children?: ReactNode }) {
  const id = useId();
  return (
    <div className="flex max-w-lg flex-col gap-1.5 font-sans text-sm">
      <p id={id} className="font-medium text-fg">
        {label}
      </p>
      <div
        role="meter"
        aria-labelledby={id}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(value, limit)}
        aria-valuetext={text}
        className="relative h-2 overflow-hidden rounded-full bg-surface-2"
      >
        <div
          className={cn('h-full rounded-full', ratio >= ALERT_LEVELS[1] ? 'bg-danger' : ratio >= ALERT_LEVELS[0] ? 'bg-warning' : 'bg-accent')}
          style={{ width: `${Math.min(1, ratio) * 100}%` }}
        />
        {ALERT_LEVELS.map((level) => (
          <div key={level} className="absolute inset-y-0 w-px bg-fg-muted" style={{ left: `${level * 100}%` }} aria-hidden="true" />
        ))}
      </div>
      <p className="text-fg">{text}</p>
      {children}
    </div>
  );
}

/**
 * Settings › Quotas (P12, spec §18.4), for the owner and admins: today's real-time requests (what decides the rooms'
 * mode), the organization's storage, and the room duration Cloudflare counts but no room can (P12 Ruling 3).
 */
export function QuotasSection({ org }: { org: Org }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const quotas = useQuery({ queryKey: ['quotas', org.id], queryFn: () => api.getQuotas(org.id) });
  const num = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(n);
  const pct = (r: number) => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 }).format(r);

  let body: ReactNode;
  if (quotas.isError) {
    body = (
      <div role="alert" className="flex max-w-lg flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-4 py-3 font-sans text-sm text-fg">
        <p className="flex-1">{t('quotas.unavailable')}</p>
        <Button variant="secondary" size="sm" onClick={() => void quotas.refetch()}>
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  } else if (!quotas.data) {
    body = <PageSkeleton label={t('auth.loading')} />;
  } else {
    const q = quotas.data;
    const reset = new Intl.DateTimeFormat(i18n.language, { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(q.resetAt));
    const mode = q.mode === 'normal' ? null : t(q.mode === 'suspended' ? 'quota.suspendedSoon' : `quota.${q.mode}`);
    body = (
      <>
        <Gauge
          label={t('quotas.requests')}
          value={q.doRequests}
          limit={QUOTAS.doRequestsPerDay}
          ratio={q.doRatio}
          text={t('quotas.of', { value: num(q.doRequests), limit: num(QUOTAS.doRequestsPerDay), percent: pct(q.doRatio) })}
        >
          {mode && <p className="font-medium text-fg">{mode}</p>}
          <p className="text-xs text-fg-muted">{t('quotas.reset', { time: reset })}</p>
          <p className="text-xs text-fg-muted">{t('quotas.requestsHint')}</p>
        </Gauge>
        <Gauge
          label={t('quotas.storage')}
          value={q.storage.used}
          limit={q.storage.limit}
          ratio={q.storage.ratio}
          text={t('quotas.of', { value: formatBytes(q.storage.used, i18n.language), limit: formatBytes(q.storage.limit, i18n.language), percent: pct(q.storage.ratio) })}
        />
        <div className="flex max-w-lg flex-col gap-1.5 font-sans text-sm">
          <p className="font-medium text-fg">{t('quotas.duration')}</p>
          <p className="text-xs text-fg-muted">{t('quotas.durationHint', { limit: num(QUOTAS.doGbSecondsPerDay) })}</p>
        </div>
      </>
    );
  }
  return <Section title={t('settings.sections.quotas')}>{body}</Section>;
}
